import {
  OrderSettlementMethod,
  OrderStatus,
  ServicePaymentMethod,
  ServiceCustomFormsRequiredFlag,
  ServiceCustomFormsType,
  ServiceFormsType,
  ServiceMenuBreakTime,
  ServiceMenuOptionTime,
  ServiceMenuTimeRequired,
  ServiceStatus,
  ServiceType,
} from "@/apis/clients";
import { FormProp } from "@/components/editors/template-blocks/service-form/form-items/FormRowsRenderer";

import { assertIsDefined } from "@/utils/common";
import { GENDER_OPTIONS } from "@/utils/const";

export type ServiceSortBy = "UPDATED" | "SALES";

export function isServiceSortBy(s: string): s is ServiceSortBy {
  return s === "UPDATED" || s === "SALES";
}

export function isServiceStatus(status: number): status is ServiceStatus {
  return (
    status === ServiceStatus.UNPUBLISHED ||
    status === ServiceStatus.PUBLISHING ||
    status === ServiceStatus.CLOSE ||
    status === ServiceStatus.DRAFT
  );
}

export type ServiceTypeGroup =
  | "all"
  | "event"
  | "calendar"
  | "attracting"
  | "other";

export function isServiceTypeGroup(g: string): g is ServiceTypeGroup {
  return (
    g === "all" ||
    g === "event" ||
    g === "calendar" ||
    g === "attracting" ||
    g === "other"
  );
}

export type ServiceSearchConditions = {
  group: ServiceTypeGroup;
  sortBy: ServiceSortBy;
  keyword: string;
  status: ServiceStatus | "all";
  updatedAtFrom: string;
  updatedAtTo: string;
};

export const ServiceStatusOptions: {
  value: ServiceStatus | "all";
  text: string;
}[] = [
  { value: "all", text: "全てのステータス" },
  { value: ServiceStatus.UNPUBLISHED, text: "非公開" },
  { value: ServiceStatus.PUBLISHING, text: "公開中" },
  { value: ServiceStatus.CLOSE, text: "終了" },
];

export const ServiceOrderOptions: {
  value: OrderStatus | "all";
  text: string;
}[] = [
  { value: "all", text: "全てのステータス" },
  { value: OrderStatus.UNPAID, text: "未入金" },
  { value: OrderStatus.BILLED, text: "決済済み" },
  // { value: OrderStatus.PAID, text: "入金済" },
  // { value: OrderStatus.COMPLETION, text: "購入・受講終了" },
  { value: OrderStatus.CANCEL, text: "キャンセル" },
  // { value: OrderStatus.WAITING, text: "キャンセル待ち" },
  // { value: OrderStatus.FINISHED, text: "現地決済" },
  { value: OrderStatus.FREE_FINISHED, text: "申し込み完了（決済なし）" },
];

export const ServiceSortOptions: {
  text: string;
  value: "UPDATED" | "SALES";
}[] = [
  { text: "更新日時の新しい順", value: "UPDATED" },
  { text: "売上順", value: "SALES" },
];

export const MAIL_DELIVERY_TYPES = {
  allow: {
    label: "最新のお知らせメールを受け取る",
    value: 1,
    boolValue: true,
    description: "現在登録済みの方は、登録状態を維持します。",
  },
  notAllow: {
    label: "お知らせメールを受け取らない",
    value: 0,
    boolValue: false,
    description: "現在登録済みの方は、今後のお知らせメールを停止します。",
  },
};

export const ServicePaymentOptions: {
  text: string;
  value: ServicePaymentMethod | string;
}[] = [
  { text: "全ての決済方法", value: "" },
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  // { text: "無料", value: ServicePaymentMethod.FREE },
  { text: "クレジットカード", value: ServicePaymentMethod.CREDIT },
  { text: "銀行振込", value: ServicePaymentMethod.BANK },
  { text: "現地払い", value: ServicePaymentMethod.LOCAL_PAYMENT },
];

export const ServiceOrderSortByOptions: {
  text: string;
  value: "BOUGHT_AT";
}[] = [{ text: "最新購入日が新しい順", value: "BOUGHT_AT" }];

export type ServiceListStatus =
  | "ステータス名"
  | "編集中"
  | "稼働中"
  | "終了"
  | "停止"
  | "下書き"
  | "審査中";

export const ServiceListStatuses: { value: ServiceListStatus; text: string }[] =
  [
    {
      text: "全てのステータス",
      value: "ステータス名",
    },
    {
      text: "編集中",
      value: "編集中",
    },
    {
      text: "稼働中",
      value: "稼働中",
    },
    {
      text: "終了",
      value: "終了",
    },
    {
      text: "停止",
      value: "停止",
    },
    {
      text: "下書き",
      value: "下書き",
    },
    {
      text: "審査中",
      value: "審査中",
    },
  ];

export function statusToText(value: ServiceListStatus): string {
  const status = ServiceListStatuses.find((s) => s.value === value);
  assertIsDefined(status);
  return status.text;
}

// TODO: API組み込み後削除
export type MockServiceType =
  | "event"
  | "calendar"
  | "school"
  | "contentDistribution";

// TODO: 詳細API組み込み後削除
export const serviceTypes: { value: MockServiceType; text: string }[] = [
  {
    value: "event",
    text: "イベント・セミナー型",
  },
  {
    value: "calendar",
    text: "カレンダー型",
  },
  {
    value: "school",
    text: "スクール型",
  },
  {
    value: "contentDistribution",
    text: "コンテンツ配信型",
  },
];

// TODO: 詳細API組み込み後削除
export function serviceTypeToText(value: MockServiceType): string {
  const type = serviceTypes.find((t) => t.value === value);
  assertIsDefined(type);
  return type.text;
}

export type PageType = "top";

export const pageTypes: { value: PageType; text: string }[] = [
  {
    value: "top",
    text: "TOP",
  },
];

export function pageTypeToText(value: PageType): string {
  const type = pageTypes.find((t) => t.value === value);
  assertIsDefined(type);
  return type.text;
}

export type ServiceState =
  | "未入金"
  | "キャンセル"
  | "キャンセル待ち"
  | "入金済"
  | "受講終了";

export type SurveyType = "graph" | "text";

export interface Service {
  id: number;
  name: string;
  status: ServiceListStatus;
  imageUrl: string;
  serviceType: ServiceType;
  content: string;
}

export interface ServiceDetail {
  id: number;
  name: string;
  status: ServiceListStatus;
  thumbnailUrl: string;
  imageUrl: string;
  serviceType: MockServiceType;
  content: string;
  formUrl: string;
  tag: string;
  pvYesterday: number;
  pvWeek: number;
  stayingTime: number;
  withdrawalRate: number;
  conversionRate: number;
  clickRate: number;
  pageType: PageType;
  updatedAt: string;
  reserves: ServiceReserve[];
}

export interface ServiceReserve {
  id: number;
  lastName: string;
  serviceName: string;
  state: ServiceState;
  reservationDate: string;
}

export interface Applicant {
  serviceId: number;
  applicants: ApplicantDetail[];
}

export interface ApplicantDetail {
  id: number;
  name: string;
  mail: string;
  tel: string;
  state: ServiceState;
}

export interface Survey {
  serviceId: number;
  surveys: SurveyDetail[];
}

export interface SurveyDetail {
  id: number;
  question: string;
  answerCount: number;
  surveyType: SurveyType;
  choices: SurveyChoice[];
  freeTexts: string[];
}

export interface SurveyChoice {
  title: string;
  number: number;
}

export function makeMockServiceDetail(id: number): ServiceDetail {
  return {
    id: id,
    name: "サービスA",
    status: "編集中",
    thumbnailUrl: "/img/service_list_icon.png",
    imageUrl: "/img/service_list_icon.png",
    serviceType: "event",
    content:
      "説明文が入ります。説明文が入ります。説明文が入ります。説明文が入ります。説明文が入ります。説明文が入ります。説明文が入ります。説明文が入ります。",
    formUrl: "http://example.com",
    tag: "tagtagtagtag",
    pvYesterday: 10,
    pvWeek: 1000,
    stayingTime: 100,
    withdrawalRate: 50.5,
    conversionRate: 20.5,
    clickRate: 70.2,
    pageType: "top",
    updatedAt: new Date("2022/04/20").toISOString(),
    reserves: [
      {
        id: 1,
        lastName: "多賀",
        serviceName: "サービスA",
        state: "未入金",
        reservationDate: new Date("2022/04/01").toISOString(),
      },
      {
        id: 2,
        lastName: "多賀",
        serviceName: "サービスB",
        state: "キャンセル",
        reservationDate: new Date("2022/04/10").toISOString(),
      },
      {
        id: 3,
        lastName: "多賀",
        serviceName: "サービスC",
        state: "入金済",
        reservationDate: new Date("2022/04/13").toISOString(),
      },
      {
        id: 4,
        lastName: "多賀",
        serviceName: "サービスD",
        state: "キャンセル待ち",
        reservationDate: new Date("2022/04/13").toISOString(),
      },
      {
        id: 5,
        lastName: "多賀",
        serviceName: "サービスE",
        state: "受講終了",
        reservationDate: new Date("2022/04/13").toISOString(),
      },
      {
        id: 6,
        lastName: "多賀",
        serviceName: "サービスF",
        state: "キャンセル待ち",
        reservationDate: new Date("2022/04/13").toISOString(),
      },
    ],
  };
}

export interface Draft {
  id: number;
  name: string;
  type: string;
  imageUrl: string;
}

export interface FormTemplate {
  id: number | string;
  iconUrl?: string;
  formType: ServiceFormsType;
  customFormsType: ServiceCustomFormsType;
  name: string;
  canDrag?: boolean;
}

/**
 * フォームタイプから、描画するフォームの内容を取得
 * @param formType
 */
export const getFormProps = (formType: ServiceFormsType): FormProp[] => {
  switch (formType) {
    // 氏名
    case ServiceFormsType.NAME:
      return [
        {
          id: "name",
          label: "氏名",
          type: "text",
          placeholder: "",
          fields: [
            {
              id: "last_name",
              fieldName: "lastName",
              label: "姓",
              type: "text",
              placeholder: "山田",
              autoComplete: "family-name",
            },
            {
              id: "first_name",
              fieldName: "firstName",
              label: "名",
              type: "text",
              placeholder: "太郎",
              autoComplete: "given-name",
            },
          ],
        },
      ];
    // フリガナ
    case ServiceFormsType.NAME_KANA:
      return [
        {
          id: "name_kana",
          label: "フリガナ",
          type: "text",
          placeholder: "",
          fields: [
            {
              id: "last_name_kana",
              fieldName: "lastNameKana",
              label: "セイ",
              type: "text",
              placeholder: "ヤマダ",
            },
            {
              id: "first_name_kana",
              fieldName: "firstNameKana",
              label: "メイ",
              type: "text",
              placeholder: "タロウ",
            },
          ],
        },
      ];
    // メールアドレス
    case ServiceFormsType.MAIL_ADDRESS:
      return [
        {
          id: "email",
          fieldName: "email",
          label: "メールアドレス",
          type: "text",
          placeholder: "sample@email.com",
          autoComplete: "email",
        },
      ];
    // メルマガ配信許可
    case ServiceFormsType.MAIL_DELIVERY:
      return [
        {
          id: "mailApproval",
          label: "お知らせメール配信",
          fieldName: "mailMagazine",
          type: "radio",
          placeholder: "",
          options: Object.values(MAIL_DELIVERY_TYPES).map((type, index) => ({
            id: index,
            value: type.value,
            label: type.label,
          })),
          defaultValue: "",
        },
      ];
    // 電話番号
    case ServiceFormsType.TEL:
      return [
        {
          id: "tel",
          label: "電話番号",
          fieldName: "tel",
          type: "tel",
          placeholder: "08000000000",
          autoComplete: "tel",
          // description: "ハイフン(-)なしで入力してください。",
        },
      ];
    // 郵便番号
    case ServiceFormsType.ZIP:
      return [
        {
          id: "postalCode",
          label: "住所",
          type: "",
          placeholder: "",
          fields: [
            {
              id: "postalCode",
              label: "郵便番号",
              type: "zip",
              fieldName: "zip1",
              placeholder: "郵便番号(ハイフンなし)",
              description: "ハイフン(-)なしで入力してください。",
              autoComplete: "postal-code",
            },
          ],
        },
      ];
    // 都道府県
    case ServiceFormsType.PREFECTURE:
      return [
        {
          id: "prefecture",
          fieldName: "prefectureId",
          label: "都道府県",
          type: "select",
          placeholder: "選択してください",
          autoComplete: "address-level1",
        },
      ];
    // 住所
    case ServiceFormsType.ADDRESS1:
      return [
        {
          id: "address",
          fieldName: "address1",
          label: "市区町村以降の住所",
          type: "text",
          placeholder: "杉並区西荻南2-24-7",
          autoComplete: "address-line1",
        },
      ];
    // 建物名
    case ServiceFormsType.ADDRESS2:
      return [
        {
          id: "building",
          fieldName: "address2",
          label: "建物名",
          type: "text",
          placeholder: "エポックビル3階",
          autoComplete: "address-line2",
        },
      ];
    // 生年月日
    case ServiceFormsType.BIRTHDAY:
      return [
        {
          id: "birthday",
          label: "生年月日",
          fieldName: "birthday",
          type: "birthday",
          placeholder: "0000/00/00",
          autoComplete: "bday",
          description:
            "数字のみで入力してください。\n例）2003年1月1日→2003/01/01",
        },
      ];
    // 年齢
    case ServiceFormsType.AGE:
      return [
        {
          id: "age",
          fieldName: "age",
          label: "年齢",
          type: "age",
          placeholder: "20",
        },
      ];
    // 性別
    case ServiceFormsType.GENDER:
      return [
        {
          id: "gender",
          fieldName: "gender",
          label: "性別",
          type: "radio",
          placeholder: "",
          options: GENDER_OPTIONS.map((item, index) => ({
            id: index,
            label: item.text,
            value: item.value,
          })),
        },
      ];
    // LINE ID
    case ServiceFormsType.LINE_ID:
      return [
        {
          id: "line",
          fieldName: "lineId",
          label: "LINE ID",
          type: "text",
          placeholder: "@yoyacoo",
        },
      ];
    // Facebook ID
    case ServiceFormsType.FACEBOOK_ID:
      return [
        {
          id: "facebook",
          fieldName: "facebookId",
          label: "Facebook ID",
          type: "text",
          placeholder: "yoyacoo",
          description:
            "<span>IDの確認方法は<a href='https://www.facebook.com/help/1503421039731588' target='_blank' style='color: #3ABCA8'>こちら</a></span>",
        },
      ];
    // Twitter ID
    case ServiceFormsType.TWITTER_ID:
      return [
        {
          id: "twitter",
          fieldName: "twitterId",
          label: "X ID",
          type: "text",
          placeholder: "@yoyacoo",
        },
      ];
    // Instagram ID
    case ServiceFormsType.INSTAGRAM_ID:
      return [
        {
          id: "instagram",
          fieldName: "instagramId",
          label: "Instagram ID",
          type: "text",
          placeholder: "@yoyacoo",
        },
      ];
    // 会社名
    case ServiceFormsType.COMPANY_NAME:
      return [
        {
          id: "companyName",
          fieldName: "companyName",
          label: "会社名",
          type: "text",
          placeholder: "株式会社ダミー",
          autoComplete: "organization",
        },
      ];
    // 業種
    case ServiceFormsType.INDUSTRY:
      return [
        {
          id: "businessType",
          fieldName: "industryId",
          label: "業種",
          type: "select",
          placeholder: "未選択",
        },
      ];
    // 役職
    case ServiceFormsType.POSITION:
      return [
        {
          id: "position",
          fieldName: "position",
          label: "役職",
          type: "text",
          placeholder: "なし",
          autoComplete: "organization-title",
        },
      ];
    // 部署名
    case ServiceFormsType.DEPARTMENT_NAME:
      return [
        {
          id: "section",
          fieldName: "department",
          label: "部署名",
          type: "text",
          placeholder: "営業部",
        },
      ];
    // 会社ホームページ
    case ServiceFormsType.COMPANY_HP:
      return [
        {
          id: "companyWebsite",
          fieldName: "companyHp",
          label: "会社ホームページ",
          type: "text",
          placeholder: "https://sample.com",
        },
      ];
    case ServiceFormsType.SINGLE_LINE_DESCRIPTION:
      return [
        {
          id: "single-line-description",
          label: "自由記述（1行）",
          description:
            "補足テキストがここに表示されます。補足テキストがここに表示されます。補足テキストがここに表示されます。",
          type: "text",
          placeholder: "",
        },
      ];
    case ServiceFormsType.MULTI_LINE_DESCRIPTION:
      return [
        {
          id: "multi-line-description",
          label: "自由記述（複数行）",
          description:
            "補足テキストがここに表示されます。補足テキストがここに表示されます。補足テキストがここに表示されます。",
          type: "area",
          placeholder: "",
        },
      ];

    case ServiceFormsType.OPTIONS:
      return [
        {
          id: "ラジオボタン",
          label: "会社ホームページ",
          description:
            "補足テキストがここに表示されます。補足テキストがここに表示されます。補足テキストがここに表示されます。",
          type: "radio",
          placeholder: "",
          options: [],
        },
      ];
    case ServiceFormsType.UPLOAD:
      return [
        {
          id: "form-upload",
          label: "ファイルアップロード",
          fieldName: "files",
          description: 
            "補足テキストがここに表示されます。補足テキストがここに表示されます。補足テキストがここに表示されます。",
          type: ServiceCustomFormsType.INPUT_UPLOAD,
          placeholder: "",
        },
      ];
  }
  return [];
};

/**
 * カテゴリー別に、フォームのテンプレートデータを返す
 * MEMO:iconUrlの値を設定しない場合、テンプレート選択リストからは除外となります。
 * @param category
 * @returns
 */
export function makeFormTemplates(category: string): FormTemplate[] {
  switch (category) {
    case "basic":
      return [
        {
          id: "nameBox",
          name: "氏名",
          formType: ServiceFormsType.NAME,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/pencil.svg",
        },
        {
          id: "kanaBox",
          name: "フリガナ",
          formType: ServiceFormsType.NAME_KANA,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/pencil.svg",
        },
        {
          id: "emailBox",
          name: "メールアドレス",
          formType: ServiceFormsType.MAIL_ADDRESS,
          customFormsType: ServiceCustomFormsType.INPUT,
        },
        {
          id: "mailApprovalBox",
          name: "メール配信許可",
          formType: ServiceFormsType.MAIL_DELIVERY,
          customFormsType: ServiceCustomFormsType.RADIO,
          iconUrl: "icon/form-parts/mail.svg",
        },
        {
          id: "telBox",
          name: "電話番号",
          formType: ServiceFormsType.TEL,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/device-mobile.svg",
        },
        {
          id: "postalCodeBox",
          name: "郵便番号",
          formType: ServiceFormsType.ZIP,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/postal.svg",
        },
        {
          id: "prefBox",
          name: "都道府県",
          formType: ServiceFormsType.PREFECTURE,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/map.svg",
        },
        {
          id: "addressBox",
          name: "住所",
          formType: ServiceFormsType.ADDRESS1,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/pin.svg",
        },
        {
          id: "buildingBox",
          name: "住所",
          formType: ServiceFormsType.ADDRESS2,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/pin.svg",
        },
        {
          id: "birthdayBox",
          name: "生年月日",
          formType: ServiceFormsType.BIRTHDAY,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/calendar.svg",
        },
        {
          id: "ageBox",
          name: "年齢",
          formType: ServiceFormsType.AGE,
          customFormsType: ServiceCustomFormsType.PULLDOWN,
          iconUrl: "icon/form-parts/age.svg",
        },
        {
          id: "genderBox",
          name: "性別",
          formType: ServiceFormsType.GENDER,
          customFormsType: ServiceCustomFormsType.RADIO,
          iconUrl: "icon/form-parts/gender.svg",
        },
      ];
    case "SNS":
      return [
        {
          id: "lineBox",
          name: "LINE ID",
          formType: ServiceFormsType.LINE_ID,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/sns-line.svg",
        },
        {
          id: "facebookBox",
          name: "Facebook ID",
          formType: ServiceFormsType.FACEBOOK_ID,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/sns-facebook.png",
        },
        {
          id: "twitterBox",
          name: "Twitter ID",
          formType: ServiceFormsType.TWITTER_ID,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/sns-twitter.svg",
        },
        {
          id: "instagramBox",
          name: "Instagram ID",
          formType: ServiceFormsType.INSTAGRAM_ID,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/sns-instagram.svg",
        },
      ];
    case "occupation":
      return [
        {
          id: "companyNameBox",
          name: "会社名",
          formType: ServiceFormsType.COMPANY_NAME,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/building.svg",
        },
        {
          id: "businessTypeBox",
          name: "業種",
          formType: ServiceFormsType.INDUSTRY,
          customFormsType: ServiceCustomFormsType.PULLDOWN,
          iconUrl: "icon/form-parts/work.svg",
        },
        {
          id: "positionBox",
          name: "役職",
          formType: ServiceFormsType.POSITION,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/identification.svg",
        },
        {
          id: "sectionBox",
          name: "部署名",
          formType: ServiceFormsType.DEPARTMENT_NAME,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/group.svg",
        },
        {
          id: "companyWebsiteBox",
          name: "会社ホームページ",
          formType: ServiceFormsType.COMPANY_HP,
          customFormsType: ServiceCustomFormsType.INPUT,
          iconUrl: "icon/form-parts/pc.svg",
        },
      ];
    default:
      return [];
  }
}

// export interface QuestionnaireTemplate {
//   id: number | string;
//   icon?: JSX.Element | ReactNode;
//   type: string;
//   name: string;
//   props: QuestionnaireProp[];
// }
// export function makeMockQuestionnaireTemplates(): QuestionnaireTemplate[] {
//   return [
//     {
//       id: "textQBox",
//       name: "テキスト",
//       type: "text",
//       props: [
//         {
//           placeholder: "",
//           data: [""],
//         },
//       ],
//     },
//     {
//       id: "pulldownQBox",
//       name: "プルダウン",
//       type: "pulldown",
//       props: [
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//       ],
//     },
//     {
//       id: "checkboxQBox",
//       name: "チェックボックス",
//       type: "questionnaire",
//       props: [
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//       ],
//     },
//     {
//       id: "radioQBox",
//       name: "ラジオボタン",
//       type: "questionnaire",
//       props: [
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//         {
//           placeholder: "回答選択肢を入力してください",
//           data: "",
//         },
//       ],
//     },
//   ];
// }

interface InitialServiceForm {
  category: string;
  id: number | string;
  required: ServiceCustomFormsRequiredFlag;
}

/**
 * フォーム作成の際にセットされるパーツの初期値
 */
export const initialSetFormTemplates: InitialServiceForm[] = [
  {
    category: "basic",
    id: "nameBox",
    required: ServiceCustomFormsRequiredFlag.REQUIRED,
  },
  {
    category: "basic",
    id: "kanaBox",
    required: ServiceCustomFormsRequiredFlag.OPTIONAL,
  },
  {
    category: "basic",
    id: "emailBox",
    required: ServiceCustomFormsRequiredFlag.REQUIRED,
  },
  {
    category: "basic",
    id: "mailApprovalBox",
    required: ServiceCustomFormsRequiredFlag.REQUIRED,
  },
];
// export function getInitialFormItems(): ServiceForm[] {
//   const items: ServiceForm[] = [];
//   initialSetFormTemplates.forEach((item) => {
//     const template = makeFormTemplates(item.category).find(
//       (template) => template.id === item.id,
//     );
//     if (template) {
//       items.push({
//         form_type: template.formType,
//         custom_forms_type: template.customFormsType,
//         required_flag: item.required,
//       });
//     }
//   });
//   return items;
// }

export type IndexServiceOrderConditions = {
  text?: string;
  receivedAtFrom?: string;
  receivedAtTo?: string;
  countFrom?: number;
  countTo?: number;
  paymentMethod?: OrderSettlementMethod;
  amountFrom?: number;
  amountTo?: number;
  holdDateFrom?: string;
  holdDateTo?: string;
  status?: OrderStatus;
  sortBy?: "BOUGHT_AT";
};

/**
 * カレンダータイプの『所要時間』
 */

export const CalendarTimeRequiredOptions: {
  value: ServiceMenuTimeRequired;
  text: string;
}[] = [
  { value: ServiceMenuTimeRequired.MINUTES_15, text: "15" },
  { value: ServiceMenuTimeRequired.MINUTES_30, text: "30" },
  { value: ServiceMenuTimeRequired.MINUTES_45, text: "45" },
  { value: ServiceMenuTimeRequired.MINUTES_60, text: "60" },
  { value: ServiceMenuTimeRequired.MINUTES_75, text: "75" },
  { value: ServiceMenuTimeRequired.MINUTES_90, text: "90" },
  { value: ServiceMenuTimeRequired.MINUTES_120, text: "120" },
  { value: ServiceMenuTimeRequired.MINUTES_150, text: "150" },
  { value: ServiceMenuTimeRequired.MINUTES_180, text: "180" },
  { value: ServiceMenuTimeRequired.MINUTES_210, text: "210" },
];

/**
 * カレンダータイプの『休憩時間』
 */

export const CalendarServiceMenuViewType: {
  value: ServiceMenuBreakTime;
  text: string;
}[] = [
  { value: ServiceMenuBreakTime.MINUTES_0, text: "0" },
  { value: ServiceMenuBreakTime.MINUTES_5, text: "5" },
  { value: ServiceMenuBreakTime.MINUTES_10, text: "10" },
  { value: ServiceMenuBreakTime.MINUTES_15, text: "15" },
  { value: ServiceMenuBreakTime.MINUTES_20, text: "20" },
  { value: ServiceMenuBreakTime.MINUTES_25, text: "25" },
  { value: ServiceMenuBreakTime.MINUTES_30, text: "30" },
];

/**
 * カレンダータイプの『オプションの時間』
 */
export const CalendarServiceMenuOptionTime: {
  value: ServiceMenuOptionTime;
  text: string;
}[] = [
  { value: ServiceMenuOptionTime.MINUTES_0, text: "0" },
  { value: ServiceMenuOptionTime.MINUTES_15, text: "15" },
  { value: ServiceMenuOptionTime.MINUTES_30, text: "30" },
  { value: ServiceMenuOptionTime.MINUTES_45, text: "45" },
  { value: ServiceMenuOptionTime.MINUTES_60, text: "60" },
  { value: ServiceMenuOptionTime.MINUTES_75, text: "75" },
  { value: ServiceMenuOptionTime.MINUTES_90, text: "90" },
  { value: ServiceMenuOptionTime.MINUTES_120, text: "120" },
  { value: ServiceMenuOptionTime.MINUTES_150, text: "150" },
  { value: ServiceMenuOptionTime.MINUTES_180, text: "180" },
  { value: ServiceMenuOptionTime.MINUTES_210, text: "210" },
];

/**
 * メニューの所要時間を取得
 */
export function getCalendarTimeRequired(time_required: number) {
  const opt = CalendarTimeRequiredOptions.find(
    (option) => option.value === time_required,
  );
  return opt?.text;
}

export interface IServiceType {
  id: number;
  label: string;
}

export interface ITemplatePage {
  id: number;
  title: string;
  imageUrl: string;
  tag?: string;
  previewSrc?: string;
}
